import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { selectSpendByCategoryCustomerReport } from '@usf/ngrx-customer-reporting';
import { Store } from '@ngrx/store';
import { PlatformEnum } from '@shared/constants/platform-type.enum';
import { PlatformService } from '@panamax/app-state';
import * as c3 from 'c3';
import * as d3 from 'd3';
import { filter, Subscription } from 'rxjs';
import { CLASS_NUMBER_TO_DESCRIPTION } from '../../constants/categories.constants';

@Component({
  selector: 'app-category-report-chart',
  templateUrl: './category-report-chart.component.html',
  styleUrls: ['./category-report-chart.component.scss'],
})
export class CategoryReportChartComponent
  implements OnInit, AfterViewInit, OnChanges, OnDestroy
{
  @Input() sortBy: string;
  @Input() cleanUp: number;
  platform: PlatformEnum;
  hasData = false;
  chart: any;
  spendByCategorySub: Subscription;
  savedDataByDollars = [];

  constructor(
    private store: Store,
    public platformService: PlatformService,
    private cdRef: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    this.platform = this.platformService.getPlatformType();
    this.spendByCategorySub = this.store
      .select(selectSpendByCategoryCustomerReport)
      .pipe(filter(res => !!res))
      .subscribe(response => {
        // If there is no data, destroy the chart (if initialized) and set hasData to false
        if (response.rows.length === 0) {
          if (this.chart) {
            this.chart = this.chart.destroy();
          }
          this.hasData = false;
          return;
        }

        // If there is data, reset the chart first
        if (this.chart) {
          this.chart = this.chart.destroy();
        }

        this.hasData = true;

        // Map Class # to Class Desc for each item before filtering
        const processedResponse = response.rows.map(item => ({
          ...item,
          'Class Desc': this.getCategoryDescription(item['Class #']),
        }));

        const cleanResponse = processedResponse.filter(
          item =>
            item['Class Desc'] !== 'UNKNOWN' &&
            item['Class Desc'] !== 'ADMINISTRATIVE' &&
            item['Dollars'] > 0,
        );

        if (cleanResponse.length === 0) {
          if (this.chart) {
            this.chart = this.chart.destroy();
          }
          this.hasData = false;
          return;
        }

        this.savedDataByDollars = cleanResponse;

        const { updatedColumnValues, updatedCategories } =
          this.transformDataForC3(cleanResponse);

        // Call chart config depending on platform
        this.platform === PlatformEnum.mobile
          ? this.generateMobileChart(updatedColumnValues, updatedCategories)
          : this.generateChart(updatedColumnValues, updatedCategories);
      });
  }

  ngOnDestroy(): void {
    if (this.spendByCategorySub) {
      this.spendByCategorySub.unsubscribe();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['cleanUp']) {
      this.hasData = false;
    }

    // Sort By Dollars or Cases client side
    if (
      changes['sortBy'] &&
      !changes['sortBy'].isFirstChange() &&
      this.hasData
    ) {
      const dataToTransform =
        this.sortBy === 'Cases'
          ? [...this.savedDataByDollars].sort((a, b) => b['Cases'] - a['Cases'])
          : [...this.savedDataByDollars].sort(
              (a, b) => b['Dollars'] - a['Dollars'],
            );

      const { updatedColumnValues, updatedCategories } =
        this.transformDataForC3(dataToTransform);

      if (this.platform === PlatformEnum.mobile) {
        setTimeout(() => {
          this.generateMobileChart(updatedColumnValues, updatedCategories);
        }, 100);
      } else {
        setTimeout(() => {
          this.generateChart(updatedColumnValues, updatedCategories);
        }, 100);
      }
    }
  }

  ngAfterViewInit() {
    this.cdRef.detectChanges();
  }

  generateMobileChart(columns: any[], categories: string[]) {
    this.chart = c3.generate({
      bindto: '#chart',
      size: {
        width: 350,
        height: 1100,
      },
      data: {
        columns: [columns],
        labels: true,
        type: 'bar',
        color: function (color, d) {
          return '#3C5B16';
        },
      },
      axis: {
        x: {
          type: 'category',
          categories,
          tick: {
            multiline: true,
          },
        },
        y: {
          show: false,
        },
        rotated: true,
      },
      legend: {
        show: false, // Hide the legend
      },
      onrendered:
        this.sortBy === 'Dollars'
          ? function () {
              d3.selectAll('.c3-texts .c3-text')
                .style('font-weight', 700)
                .style('font-size', '12px')
                .style('fill', '#000')
                .style('font-family', 'SF Pro Display, sans-serif')
                .text(function (d) {
                  const formatter = new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD',
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  });
                  return formatter.format(d.value);
                });

              d3.selectAll('.c3-axis-x text')
                .style('font-size', '10px')
                .style('max-width', '40px')
                .style('white-space', 'normal');
            }
          : function () {
              d3.selectAll('.c3-texts .c3-text')
                .style('font-weight', 700)
                .style('font-size', '10px')
                .style('fill', '#000')
                .style('font-family', 'SF Pro Display, sans-serif')
                .text(function (d) {
                  return Number.isInteger(d.value)
                    ? d.value.toLocaleString()
                    : d.value.toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      });
                });
            },
      tooltip: {
        show: true,
        format: {
          value:
            this.sortBy === 'Dollars'
              ? function (value, ratio, id) {
                  return '$' + value.toFixed(2);
                }
              : function (value, ratio, id) {
                  return value;
                },
        },
      },
    });

    // Trigger rerender for better layout
    setTimeout(() => {
      this.chart.flush();
    }, 200);
  }

  // Desktop/tablet chart
  generateChart(columns: any[], categories: string[]) {
    this.chart = c3.generate({
      bindto: '#chart',
      size: {
        height: 315,
        width: this.platform === PlatformEnum.desktop ? 2100 : 2500, // tablet 2500, desktop 1900
      },
      data: {
        columns: [columns],
        labels: true,
        type: 'bar',
        color: function (color, d) {
          // Custom coloring, adjust as needed
          return '#3C5B16'; // Single color for all bars (customize if needed)
        },
      },
      bar: {
        width: {
          ratio: 0.65,
        },
        padding: {
          left: 30, // Adds space on the left of each bar
          right: 30, // Adds space on the right of each bar
        },
      },
      axis: {
        x: {
          type: 'category',
          categories,
          tick: {
            centered: true,
          },
        },
        y: {
          min: 0,
          max: columns[1] + columns[1] * 0.1,
          padding: { top: 0, bottom: 0 },
          show: false,
        },
      },
      legend: {
        show: false, // Hide the legend
      },
      onrendered:
        this.sortBy === 'Dollars'
          ? function () {
              // Apply styles to the labels on top of bars
              d3.selectAll('.c3-texts .c3-text')
                .style('font-weight', 700) // Make the labels bold
                .style('font-size', '13px') // Adjust the font size
                .style('fill', '#000') // Set label color to black
                .style('padding-bottom', '30px')
                .style('font-family', 'SF Pro Display, sans-serif') // Set the font family
                .text(function (d) {
                  // Format the number with a dollar sign and commas
                  const formatter = new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD',
                    minimumFractionDigits: 2, // Ensure 2 decimal places
                    maximumFractionDigits: 2, // Ensure 2 decimal places
                  });
                  return formatter.format(d.value); // Format the value with commas and $ sign
                });

              // Adjust label position using `dy` (vertical offset)
              d3.selectAll('.c3-texts .c3-text').attr('dy', '-10px'); // Move labels up by 5px to prevent overlap with bars
              d3.selectAll('.c3-axis-x text').attr(
                'transform',
                'translate(0, 10)',
              ); // Adjust 10 to move the labels down (positive value moves down)
            }
          : function () {
              // Apply styles to the labels on top of bars
              d3.selectAll('.c3-texts .c3-text')
                .style('font-weight', 700) // Make the labels bold
                .style('font-size', '14px') // Adjust the font size
                .style('fill', '#000') // Set label color to black
                .style('padding-bottom', '30px')
                .style('font-family', 'SF Pro Display, sans-serif')
                .text(function (d) {
                  return Number.isInteger(d.value)
                    ? d.value.toLocaleString()
                    : d.value.toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      });
                });

              // Adjust label position using `dy` (vertical offset)
              d3.selectAll('.c3-texts .c3-text').attr('dy', '-10px'); // Move labels up by 5px to prevent overlap with bars
              d3.selectAll('.c3-axis-x text').attr(
                'transform',
                'translate(0, 10)',
              ); // Adjust 10 to move the labels down (positive value moves down)
            },
      padding: {
        bottom: 30, // Increase the bottom padding to accommodate the labels
      },
      tooltip: {
        show: true, // Ensure the tooltip is enabled
        format: {
          value:
            this.sortBy === 'Dollars'
              ? function (value, ratio, id) {
                  return '$' + value.toFixed(2); // Only show the value as currency
                }
              : function (value, ratio, id) {
                  return value;
                },
        },
      },
    });

    // Trigger rerender for better layout
    setTimeout(() => {
      this.chart.flush();
    }, 200);
  }

  // Transform data for c3.js chart
  transformDataForC3 = (data: any[]) => {
    const categoriesFromResponse = data.map(item => item['Class Desc']);

    // Create a set of all possible categories from the enum, sorted alphabetically
    const allCategories = Object.values(CLASS_NUMBER_TO_DESCRIPTION).sort();

    // Initialize category data with 0 for missing categories
    const categoryData: Record<string, number> = {};

    // Add existing categories with actual values
    data.forEach(item => {
      categoryData[item['Class Desc']] =
        item[this.sortBy === 'Dollars' ? 'Dollars' : 'Cases'];
    });

    // Add missing categories with 0 value
    allCategories.forEach(category => {
      if (!categoriesFromResponse.includes(category)) {
        categoryData[category] = 0; // Add missing categories with value 0
      }
    });

    // Maintain the order of categories from the response and append the missing ones sorted alphabetically
    const updatedCategories = [
      ...categoriesFromResponse,
      ...allCategories.filter(
        category => !categoriesFromResponse.includes(category),
      ),
    ];

    const updatedColumnValues = [
      this.sortBy === 'Dollars' ? 'Spent' : 'Bought',
      ...updatedCategories.map(category => categoryData[category]),
    ];

    return { updatedColumnValues, updatedCategories };
  };

  // Get category description by class number
  private getCategoryDescription(classNumber: number): string {
    return CLASS_NUMBER_TO_DESCRIPTION[classNumber] || 'UNKNOWN';
  }
}
